import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Skeleton, notification } from 'antd';
import {
    CaretLeft,
    CaretRight,
    Phone,
    UserCircle,
    UsersThree,
    IdentificationBadge,
    Scroll,
    UserPlus,
} from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import opportunityInfoService from '../../../../services/oneButtonServices/opportunityInfoService';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import { ContactManageModal, type ContactFormState } from './ContactManageModal';
import type { ContactManageModalProps } from './ContactManageModal';
import { TitleInfoModal } from './TitleInfoModal';

export interface ContactsSnapshotCardProps {
    loading?: boolean;
    opportunityId?: string | null;
    isTitleContactExist?: boolean | null;
    onAddTitleContactsClick?: () => void;
    onBlackKnightTitleCheckClick?: () => void;
    /** Split modal: contact list left, edit form right (no nested contact modal) */
    layout?: 'card' | 'modalSplit';
}

/** Tag row styling: PRIMARY = accent; role labels = muted caps; Credit Passed = success */
export type ContactSnapshotTagTone = 'primary' | 'neutral' | 'success';

export interface ContactSnapshotTag {
    key: string;
    label: string;
    tone: ContactSnapshotTagTone;
}

function checkFinancePartnerCreditPassStatus(cust: any): boolean {
    if (!cust) return false;
    const creditStatusList =
        cust.financePartnerCreditStatusList?.CreditStatusList || cust.financePartnerCreditStatusList?.creditStatusList || [];
    const syncContactId = cust.Finance_Partner_Sync_ContactId || cust.finance_Partner_Sync_ContactId;
    if (creditStatusList.length > 0) {
        const val: number[] = [];
        creditStatusList.forEach((ele: any) => {
            const eleCheckStatus = ele.creditCheckStatus ?? ele.CreditCheckStatus ?? ele.IsCreditPass ?? ele.isCreditPass;
            if (eleCheckStatus === true && syncContactId) {
                const expireOn = ele.expireOn ?? ele.ExpireOn;
                if (expireOn) {
                    const expiryDate = new Date(expireOn).getTime();
                    const date = new Date().getTime();
                    if (expiryDate < date) {
                        if (!val.includes(1)) val.push(1);
                    } else {
                        if (!val.includes(2)) val.push(2);
                    }
                } else {
                    if (!val.includes(2)) val.push(2);
                }
            } else {
                if (!val.includes(3)) val.push(3);
            }
        });
        return val.includes(2);
    }
    return false;
}

function getSunlightCreditCheckStatus(sunlightStatus: any): number {
    if (!sunlightStatus) return 5;
    const date = new Date().getTime();
    const expireOn = sunlightStatus.ExpireOn ?? sunlightStatus.expireOn;
    const creditStatus = sunlightStatus.CreditStatus ?? sunlightStatus.creditStatus;
    if (creditStatus && expireOn && !creditStatus.includes('Decline')) {
        const expiryDate = new Date(expireOn).getTime();
        if (expiryDate < date && creditStatus !== null) {
            return 1;
        }
        return 2;
    }
    if (creditStatus === 'Declined' || creditStatus === 'declined' || creditStatus?.includes('Decline')) {
        return 4;
    }
    if (creditStatus === null || creditStatus === undefined) {
        return 5;
    }
    return 3;
}

/** Align with ContactManageModal / FinancePartnerChecklistModal credit signals */
function contactHasPassedCredit(
    c: any,
    isPrimary: boolean,
    options?: { sunlightCreditStatus?: any; isSunlightPartnerExist?: boolean },
): boolean {
    const hasCreditCheckStatus = c?.CreditCheckStatus === true || c?.creditCheckStatus === true;

    const passStr = String(c?.CreditPassStatus || c?.creditPassStatus || '').toLowerCase();
    const isBaseCreditPass =
        checkFinancePartnerCreditPassStatus(c) ||
        c?.financePartnerCreditStatusList?.IsAnyCreditStatusPass === true ||
        passStr === 'passed';

    const isSunlightPass =
        !!options?.isSunlightPartnerExist &&
        isPrimary &&
        getSunlightCreditCheckStatus(options?.sunlightCreditStatus) === 2;

    return !!((isBaseCreditPass || isSunlightPass) && hasCreditCheckStatus);
}

function getContactDisplay(
    c: any,
    override?: Partial<ContactFormState>,
    options?: { sunlightCreditStatus?: any; isSunlightPartnerExist?: boolean },
) {
    const firstName = override?.firstName ?? c?.FirstName ?? '';
    const lastName = override?.lastName ?? c?.LastName ?? '';
    const name = `${firstName} ${lastName}`.trim() || 'No name';
    const phone = override?.phone ?? c?.Phone ?? '';
    const tags: ContactSnapshotTag[] = [];

    const isPrimary = override?.isPrimaryContact ?? !!(c?.IsPrimaryContact || c?.isPrimaryContact);
    if (isPrimary) tags.push({ key: 'primary', label: 'PRIMARY', tone: 'primary' });

    const isSecondary = override?.isSecondaryContact ?? !!(c?.IsSecondaryContact || c?.isSecondaryContact);
    if (isSecondary) tags.push({ key: 'secondary', label: 'SECONDARY CONTACT', tone: 'neutral' });

    const onUtility = override?.onUtilityBill ?? !!(c?.IsUtilityBillOwner || c?.isUtilityBillOwner);
    if (onUtility) tags.push({ key: 'utility', label: 'ON UTILITY', tone: 'neutral' });

    const onTitle = override?.onTitle ?? !!(c?.OnTitleCheck || c?.onTitleCheck);
    const isHO = onTitle ? false : !!(override?.homeowner ?? (c?.IsHomeowner || c?.isHomeowner));

    if (isHO) tags.push({ key: 'homeowner', label: 'HOMEOWNER', tone: 'neutral' });
    if (onTitle) tags.push({ key: 'title', label: 'ON TITLE', tone: 'neutral' });

    const creditPassed = contactHasPassedCredit(c, isPrimary, options);

    return { name, phone, tags, creditPassed, firstName, lastName };
}

function contactInitials(firstName: string, lastName: string): string {
    const ini = `${String(firstName).trim()[0] ?? ''}${String(lastName).trim()[0] ?? ''}`.toUpperCase();
    return ini || '?';
}

/** Modal list + detail hero: only primary / secondary role labels. */
function getPrimaryOrSecondaryTag(tags: ContactSnapshotTag[]): ContactSnapshotTag | undefined {
    return tags.find((t) => t.key === 'primary') ?? tags.find((t) => t.key === 'secondary');
}

const MODAL_HERO_TAG_ORDER = ['primary', 'secondary', 'utility', 'homeowner', 'title'] as const;

/** Detail hero: primary/secondary plus other active contact-type flags. */
function getHeroDisplayTags(tags: ContactSnapshotTag[]): ContactSnapshotTag[] {
    return MODAL_HERO_TAG_ORDER.map((key) => tags.find((t) => t.key === key)).filter(
        (t): t is ContactSnapshotTag => !!t,
    );
}

/** Short labels for V2 split modal list / hero (not full card tag row). */
function formatModalRoleLabel(tag: ContactSnapshotTag): string {
    if (tag.key === 'primary') return 'Primary';
    if (tag.key === 'secondary') return 'Secondary';
    if (tag.key === 'utility') return 'On utility';
    if (tag.key === 'homeowner') return 'Homeowner';
    if (tag.key === 'title') return 'On title';
    return tag.label;
}

/**
 * Contacts + Lead & Title on one card: contact rows, lead fields, footer with AHJ / office and actions.
 */
export const ContactsSnapshotCard: React.FC<ContactsSnapshotCardProps> = ({
    loading: propsLoading = false,
    opportunityId,
    isTitleContactExist = null,
    onAddTitleContactsClick,
    onBlackKnightTitleCheckClick,
    layout = 'card',
}) => {
    const isModalSplit = layout === 'modalSplit';
    const [contactOverrides, setContactOverrides] = useState<Record<number, Partial<ContactFormState>>>({});
    const [modalContactIndex, setModalContactIndex] = useState<number | null>(null);
    const dashboardContactsOpenNewContact = useOneButtonStore((s) => s.dashboardContactsOpenNewContact);
    const setDashboardContactsOpenNewContact = useOneButtonStore((s) => s.setDashboardContactsOpenNewContact);

    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const setOpportunityDetails = useOneButtonStore((s) => s.setOpportunityDetails);
    const setQuotesRefreshTrigger = useOneButtonStore((s) => s.setQuotesRefreshTrigger);
    const customers: any[] = opportunityDetails?.Customers ?? [];

    const [sunlightCreditStatus, setSunlightCreditStatus] = useState<any>(null);

    const isSunlightPartnerExist = useMemo(() => {
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        return products.some((p: any) => p.Finance_Partner === 'Sunlight' || p.Finance_Partner_Display_Name === 'Sunlight');
    }, [opportunityDetails]);

    useEffect(() => {
        const fetchSunlight = async () => {
            if (!opportunityDetails?.Opportunity_Id || !isSunlightPartnerExist) {
                setSunlightCreditStatus(null);
                return;
            }
            try {
                const res = await opportunityInfoService.getSunlightCreditStatus(opportunityDetails.Opportunity_Id);
                if (res && res.length) {
                    setSunlightCreditStatus(res[0]);
                } else {
                    setSunlightCreditStatus(null);
                }
            } catch {
                setSunlightCreditStatus(null);
            }
        };
        fetchSunlight();
    }, [opportunityDetails?.Opportunity_Id, isSunlightPartnerExist]);

    const [saving, setSaving] = useState(false);
    const loading = propsLoading || saving;
    const theme = useOneButtonStore((s) => s.theme);
    const titleInfo = useOneButtonStore((s) => s.titleInfo) as Record<string, unknown> | null;

    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const opportunityInfoAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Opportunity_Information_Access');
    const isOpportunityInfoDisabled = opportunityInfoAccess !== 'Edit' && opportunityInfoAccess !== 'View';
    const [titleInfoModalOpen, setTitleInfoModalOpen] = useState(false);
    const [noticeAPI, contextHolder] = notification.useNotification();


    const highlight = opportunityDetails?.OpportunityHighlight ?? {};
    const leadSource = (highlight as { LeadSource?: string }).LeadSource ?? '';
    const ahj = (highlight as { AHJ?: string }).AHJ ?? '';
    const managingOffice = (highlight as { Managing_Office?: string }).Managing_Office ?? '';

    // -- Helpers for optimization --
    const showNotice = useCallback((type: 'success' | 'error' | 'warning' | 'info', message: string, description?: string) => {
        noticeAPI[type]({
            message,
            description,
            placement: 'topRight',
            className: `obd-notification obd-theme-${theme}`,
            style: { zIndex: 10000 }
        });
    }, [noticeAPI, theme]);

    const refreshOpportunityData = useCallback(async () => {
        if (!opportunityDetails?.SalesForce_Id) return;
        try {
            const updatedDetails = await opportunityInfoService.getOpportunityDetail(opportunityDetails.SalesForce_Id);
            const actualData = updatedDetails?.data ?? updatedDetails;
            // `getOpportunityDetail` can return `null` (e.g. 400 from API for "no data").
            // Avoid clearing the store, which makes the UI look stale/blank after a successful save.
            if (actualData) {
                setOpportunityDetails(actualData);
                setContactOverrides({});
            }
        } catch (err) {
            console.error("Failed to refresh opportunity data", err);
        }
    }, [opportunityDetails?.SalesForce_Id, setOpportunityDetails]);

    const mergedCustomers = useMemo(() => {
        const isLightreach = opportunityDetails?.opportunityProduct?.[0]?.Finance_Partner === 'Lightreach';
        
        let primaryFound = false;

        return customers.map((c, i) => {
            const override = contactOverrides[i];
            
            // a) Determine Primary Role
            let isThisPrimary = false;
            // Check override first, then original
            if (override?.isPrimaryContact !== undefined) {
                isThisPrimary = override.isPrimaryContact;
            } else {
                isThisPrimary = !!(c.IsPrimaryContact || c.isPrimaryContact);
            }

            // Enforce single primary rule
            if (isThisPrimary) {
                if (primaryFound) isThisPrimary = false;
                else primaryFound = true;
            }

            // b) Determine Secondary Role (Secondary if original was secondary and not now primary)
            const originalSecondary = !!(c.IsSecondaryContact || c.isSecondaryContact);
            const isThisSecondary = override?.isSecondaryContact !== undefined 
                ? override.isSecondaryContact 
                : (originalSecondary && !isThisPrimary);

            // c) Determine AddToContract Role
            const originalContract = !!(c.AddToContract || c.addToContract);
            let isThisContract = override?.addToContract !== undefined
                ? override.addToContract
                : originalContract;

            return { 
                ...c, 
                // Force sync role flags so Modal and Display both see consistent state
                IsPrimaryContact: isThisPrimary,
                isPrimaryContact: isThisPrimary,
                IsSecondaryContact: isThisSecondary,
                isSecondaryContact: isThisSecondary,
                AddToContract: isThisContract,
                addToContract: isThisContract,
                _override: override,
            };
        });
    }, [customers, contactOverrides, opportunityDetails]);

    useEffect(() => {
        if (!isModalSplit || !dashboardContactsOpenNewContact) return;
        setModalContactIndex(customers.length);
        setDashboardContactsOpenNewContact(false);
    }, [
        isModalSplit,
        dashboardContactsOpenNewContact,
        customers.length,
        setDashboardContactsOpenNewContact,
    ]);

    useEffect(() => {
        if (!isModalSplit || propsLoading) return;
        if (mergedCustomers.length === 0) {
            setModalContactIndex(null);
            return;
        }
        setModalContactIndex((idx) => {
            if (idx === null || idx >= mergedCustomers.length) return 0;
            return idx;
        });
    }, [isModalSplit, mergedCustomers.length, propsLoading]);

    const handleSaveContact: ContactManageModalProps['onSave'] = async (index: number, data: ContactFormState, emailWarning?: string) => {
        if (!opportunityDetails?.SalesForce_Id) return;

        if (emailWarning) {
            showNotice('warning', 'Check Primary and Secondary Email Addresses', emailWarning);
        }

        try {
            setSaving(true);
            
            // Map existing customers and potentially append a new one
            const updatedCustomerList = customers.map((cust, i) => {
                const isTarget = i === index;
                const custId = cust?.Id || cust?.CustomerId || 0;
                
                const baseFields = {
                    FirstName: isTarget ? data.firstName : (cust?.FirstName || ''),
                    LastName: isTarget ? data.lastName : (cust?.LastName || ''),
                    Email: isTarget ? data.email : (cust?.Email || ''),
                    Phone: isTarget ? data.phone : (cust?.Phone || ''),
                    PhoneType: isTarget ? parseInt(data.phoneType, 10) : (cust?.PhoneType || 2),
                    ContactType: isTarget ? parseInt(data.contactType, 10) : (cust?.ContactType || 77),
                    PreferredContactMethod: isTarget ? parseInt(data.preferredContactMethod, 10) : (cust?.PreferredContactMethod || 6),
                    PreferredLanguage: isTarget ? parseInt(data.preferredLanguage, 10) : (cust?.PreferredLanguage || 4),
                    SameAsInstallationAddress: isTarget ? data.sameAsInstallationAddress : (cust?.SameAsInstallationAddress ?? true),
                    InsStreet: isTarget ? data.mailingStreet : (cust?.InsStreet || cust?.Street || ''),
                    InsCity: isTarget ? data.mailingCity : (cust?.InsCity || cust?.City || ''),
                    InsState: isTarget ? data.mailingState : (cust?.InsState || cust?.State || ''),
                    InsZipCode: isTarget ? data.mailingZip : (cust?.InsZipCode || cust?.ZipCode || ''),
                    IsPrimaryContact: isTarget ? data.isPrimaryContact : (data.isPrimaryContact ? false : (cust?.IsPrimaryContact || false)),
                    IsSecondaryContact: isTarget ? data.isSecondaryContact : (data.isSecondaryContact ? false : (cust?.IsSecondaryContact || false)),
                    IsUtilityBillOwner: isTarget ? data.onUtilityBill : (cust?.IsUtilityBillOwner ?? false),
                    IsHomeOwner: isTarget ? data.homeowner : (cust?.IsHomeowner ?? false),
                    AddToContract: isTarget ? data.addToContract : (cust?.AddToContract ?? false),
                    OnTitleCheck: isTarget ? data.onTitle : (cust?.OnTitleCheck ?? false),
                };

                return {
                    ...cust,
                    ...baseFields,
                    CustomerId: custId,
                    index: i + 1,
                };
            });

            // Handle new contact append
            if (index === customers.length) {
                updatedCustomerList.push({
                    CustomerId: 0,
                    FirstName: data.firstName,
                    LastName: data.lastName,
                    Email: data.email,
                    Phone: data.phone,
                    PhoneType: data.phoneType ? parseInt(data.phoneType, 10) : 2,
                    ContactType: data.contactType ? parseInt(data.contactType, 10) : 77,
                    PreferredContactMethod: data.preferredContactMethod ? parseInt(data.preferredContactMethod, 10) : 6,
                    PreferredLanguage: data.preferredLanguage ? parseInt(data.preferredLanguage, 10) : 4,
                    SameAsInstallationAddress: data.sameAsInstallationAddress,
                    InsStreet: data.mailingStreet,
                    InsCity: data.mailingCity,
                    InsState: data.mailingState,
                    InsZipCode: data.mailingZip,
                    IsPrimaryContact: data.isPrimaryContact,
                    IsSecondaryContact: data.isSecondaryContact,
                    IsUtilityBillOwner: data.onUtilityBill,
                    AddToContract: data.addToContract,
                    IsHomeOwner: data.homeowner,
                    OnTitleCheck: data.onTitle,
                    index: customers.length + 1,
                });
            }

            const payload = {
                SalesForce_Id: opportunityDetails.SalesForce_Id,
                opportunityId: opportunityDetails.Opportunity_Id,
                InstallationAddress: opportunityDetails.InstallationAddress ? {
                    street: opportunityDetails.InstallationAddress.Street || '',
                    city: opportunityDetails.InstallationAddress.City || '',
                    state: opportunityDetails.InstallationAddress.State || opportunityDetails.InstallationAddress.stateCode || '',
                    zipCode: opportunityDetails.InstallationAddress.ZipCode || opportunityDetails.InstallationAddress.zipCode || '',
                    latitude: opportunityDetails.InstallationAddress.Latitude || 0,
                    longitude: opportunityDetails.InstallationAddress.Longitude || 0
                } : null,
                customerInformation: updatedCustomerList
            };

            const response = await opportunityInfoService.updateContactInformation(payload);
            const isSuccess = response?.Status === 0 || response?.status === 0 || !response?.Status;

            if (isSuccess) {
                // Update the UI immediately with our local list (so the user sees changes even if
                // the follow-up OpportunityDetail call is delayed / returns null transiently).
                setOpportunityDetails({
                    ...(opportunityDetails ?? {}),
                    Customers: updatedCustomerList,
                });
                showNotice('success', index === customers.length ? 'Contact Created' : 'Contact Updated', 'The changes have been saved successfully.');
                if (!isModalSplit) setModalContactIndex(null);
                await refreshOpportunityData();
                runContractSaveUpdateLoanStatusFlow();

            } else {
                showNotice('error', 'Save Error', response?.Message || 'Failed to save contact information.');
            }
        } catch (error) {
            console.error("Failed to update contact information", error);
            showNotice('error', 'Unexpected Error', 'An error occurred while saving. Please try again.');
            // Fallback to local override if API fails
            setContactOverrides(prev => ({ ...prev, [index]: data }));
            if (!isModalSplit) setModalContactIndex(null);
        } finally {
            setSaving(false);
        }
    };

    const runContractSaveUpdateLoanStatusFlow = async () =>{
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct;
        let haveGoodLeapProduct = false;
        let haveLightreachProduct = false;
        (products || []).forEach((product: any) => {
            if (product.ActiveFlag && product.Finance_Partner.toLowerCase() === 'goodleap') haveGoodLeapProduct = true;
            if (product.Finance_Partner.toLowerCase() === 'lightreach') haveLightreachProduct = true;
        });
        if (!haveGoodLeapProduct && !haveLightreachProduct) return;
        try {
            await quoteServices.contractUpdateLoanStatus(opportunityDetails.Opportunity_Id, 'Contact', 0);
        } catch {
            return;
        }
        try {
            const data = await quoteServices.contractGetLoanStatusByOpportunity(opportunityDetails.Opportunity_Id);
            setQuotesRefreshTrigger(Date.now());
            if (Array.isArray(data) && data.some((detail: any) => detail.Is_Change_Order)) {
                if (!haveLightreachProduct) {
                    showNotice('error', 'Unexpected Error', 'New quote is required due to changes in utility information before change order');
                };
            }
        } catch {
            /* getLoanStatus failure: Angular only clears loader */
        }
    }

    const handleDeleteContact = async (index: number) => {
        const c = mergedCustomers[index];
        const contactId = c?.Id || c?.CustomerId;
        const displayName = `${c?.FirstName || ''} ${c?.LastName || ''}`.trim() || 'Contact';

        if (!contactId || !opportunityDetails?.SalesForce_Id) {
            showNotice('error', 'Error', 'Cannot delete contact: missing ID.');
            return;
        }

        try {
            setSaving(true);
            const response = await opportunityInfoService.removeContact(String(contactId));
            const isSuccess = response?.Status === 0 || response?.status === 0 || !response?.Status;

            if (isSuccess) {
                showNotice('success', 'Contact Removed', `${displayName} was successfully removed.`);
                await refreshOpportunityData();
            } else {
                showNotice('error', 'Delete Error', response?.Message || 'Failed to delete contact.');
            }
        } catch (error: any) {
            console.error("Failed to delete contact", error);
            const errorMessage = error?.response?.data?.Message || error?.response?.data?.message || error?.message || 'Deletion Failed. Please try again.';
            showNotice('error', 'Delete Error', errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const contactRows = (
        <div className="obd-contacts-body">
            {loading ? (
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
            ) : mergedCustomers.length === 0 ? (
                <div className="obd-contact-cards-empty">No contacts</div>
            ) : (
                <div className="obd-contact-rows" role="list">
                    {mergedCustomers.map((customer, index) => {
                        const display = getContactDisplay(customer, customer._override, {
                            sunlightCreditStatus,
                            isSunlightPartnerExist,
                        });
                        const roleTags = display.tags;
                        const isSelected = isModalSplit && modalContactIndex === index;
                        const initials = contactInitials(display.firstName, display.lastName);

                        if (isModalSplit) {
                            const listRoleTag = getPrimaryOrSecondaryTag(roleTags);
                            return (
                                <button
                                    key={index}
                                    type="button"
                                    className={`obd-contact-row obd-contact-row--modal${isSelected ? ' obd-contact-row--selected' : ''}`}
                                    onClick={() => setModalContactIndex(index)}
                                    role="listitem"
                                    aria-label={`View or edit ${display.name}`}
                                    aria-current={isSelected ? 'true' : undefined}
                                >
                                    <span className="obd-contact-row__avatar" aria-hidden>
                                        {initials}
                                    </span>
                                    <span className="obd-contact-row__stack">
                                        <span className="obd-contact-row__name">{display.name}</span>
                                        {display.phone ? (
                                            <span className="obd-contact-row__subline">
                                                <span className="obd-contact-row__phone-inline">{display.phone}</span>
                                            </span>
                                        ) : null}
                                    </span>
                                    {listRoleTag ? (
                                        <span
                                            className={`obd-contact-row__pill obd-contact-row__pill--rail obd-contact-row__pill--${listRoleTag.tone}`}
                                        >
                                            {formatModalRoleLabel(listRoleTag)}
                                        </span>
                                    ) : null}
                                    <CaretRight
                                        size={14}
                                        weight="bold"
                                        className="obd-contact-row__chevron"
                                        aria-hidden
                                    />
                                </button>
                            );
                        }

                        const rowColumns = (
                            <>
                                <div className="obd-contact-row__left">
                                    <span className="obd-contact-row__name">{display.name}</span>
                                    {roleTags.length > 0 ? (
                                        <div
                                            className="obd-contact-row__tags-row"
                                            role="list"
                                            aria-label="Contact roles"
                                        >
                                            {roleTags.map((tag, tagIndex) => (
                                                <span
                                                    key={`${tag.key}-${tagIndex}`}
                                                    className={`obd-contact-row__tag obd-contact-row__tag--${tag.tone}`}
                                                    role="listitem"
                                                >
                                                    {tag.label}
                                                </span>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="obd-contact-row__right">
                                    <div className="obd-contact-row__right-stack">
                                        <span className="obd-contact-row__phone">
                                            <Phone size={12} weight="regular" aria-hidden />
                                            {display.phone || '—'}
                                        </span>
                                        {display.creditPassed ? (
                                            <span className="obd-contact-row__tag obd-contact-row__tag--success obd-contact-row__credit">
                                                Credit Passed
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            </>
                        );
                        return (
                            <button
                                key={index}
                                type="button"
                                className={`obd-contact-row${isSelected ? ' obd-contact-row--selected' : ''}`}
                                onClick={() => setModalContactIndex(index)}
                                role="listitem"
                                aria-label={`View or edit ${display.name}`}
                                aria-current={isSelected ? 'true' : undefined}
                            >
                                {isModalSplit ? (
                                    <span className="obd-contact-row__avatar" aria-hidden>
                                        {initials}
                                    </span>
                                ) : null}
                                {isModalSplit ? (
                                    <div className="obd-contact-row__main">{rowColumns}</div>
                                ) : (
                                    rowColumns
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const contactsFooter = (
        <footer className="obd-dashboard-main-card__foot obd-contacts-footer obd-contacts-footer--merged">
            <div className="obd-contacts-footer__meta" aria-label="Jurisdiction and office">
                <div className="obd-contacts-footer__meta-item">
                    <span className="obd-contacts-footer__meta-label">AHJ</span>
                    <span className="obd-contacts-footer__meta-value">{ahj || '—'}</span>
                </div>
                <div className="obd-contacts-footer__meta-item">
                    <span className="obd-contacts-footer__meta-label">Managing Office</span>
                    <span className="obd-contacts-footer__meta-value">{managingOffice || '—'}</span>
                </div>
            </div>
            <div
                className="obd-contacts-footer__actions obd-metrics__action-group"
                role="group"
                aria-label="Contact actions"
            >
                <button
                    type="button"
                    className="obd-merged-system__chip-btn"
                    onClick={() => setTitleInfoModalOpen(true)}
                    aria-label="Open title information"
                >
                    <Scroll size={16} weight="bold" aria-hidden />
                    <span>Title Info</span>
                </button>
                {isTitleContactExist !== null ? (
                    <button
                        type="button"
                        className="obd-merged-system__chip-btn"
                        aria-label="Add title contacts"
                        onClick={
                            isTitleContactExist === true
                                ? onAddTitleContactsClick
                                : onBlackKnightTitleCheckClick
                        }
                    >
                        <IdentificationBadge size={16} weight="bold" aria-hidden />
                        <span>Add Title Contacts</span>
                    </button>
                ) : null}
                <button
                    type="button"
                    className="obd-merged-system__chip-btn"
                    onClick={() => setModalContactIndex(customers.length)}
                    aria-label="Add contact"
                >
                    <UserPlus size={16} weight="bold" aria-hidden />
                    <span>Add Contact</span>
                </button>
            </div>
        </footer>
    );

    const contactEditor =
        modalContactIndex !== null ? (
            <ContactManageModal
                open
                presentation={isModalSplit ? 'panel' : 'modal'}
                onClose={() => setModalContactIndex(null)}
                contact={
                    modalContactIndex < mergedCustomers.length ? mergedCustomers[modalContactIndex] : null
                }
                contactIndex={modalContactIndex ?? 0}
                initialOverride={
                    modalContactIndex !== null && modalContactIndex < customers.length
                        ? contactOverrides[modalContactIndex]
                        : undefined
                }
                onSave={handleSaveContact}
                onDelete={handleDeleteContact}
                onDeleteNew={() => setModalContactIndex(0)}
                contactOverrides={contactOverrides}
                onFormChange={
                    isModalSplit && modalContactIndex !== null
                        ? (data) => {
                              setContactOverrides((prev) => ({
                                  ...prev,
                                  [modalContactIndex]: { ...prev[modalContactIndex], ...data },
                              }));
                          }
                        : undefined
                }
            />
        ) : null;

    if (isModalSplit) {
        const detailOpen = modalContactIndex !== null;
        const activeDetailDisplay =
            modalContactIndex !== null && modalContactIndex < mergedCustomers.length
                ? getContactDisplay(mergedCustomers[modalContactIndex], mergedCustomers[modalContactIndex]._override, {
                      sunlightCreditStatus,
                      isSunlightPartnerExist,
                  })
                : null;
        const detailHeading =
            modalContactIndex === null
                ? null
                : activeDetailDisplay?.name ?? 'New contact';
        const detailInitials =
            activeDetailDisplay != null
                ? contactInitials(activeDetailDisplay.firstName, activeDetailDisplay.lastName)
                : '+';

        return (
            <div
                className={`obd-contacts-modal-split obd-theme-${theme}${detailOpen ? ' obd-contacts-modal-split--detail-open' : ''} ${isOpportunityInfoDisabled ? 'obd-disabled-section' : ''}`}
            >
                {contextHolder}
                <div className="obd-contacts-modal-split__list">
                    <div className="obd-contacts-modal-split__list-rail">
                        <div className="obd-contacts-modal-split__list-head">
                            <h3 className="obd-contacts-v2__title obd-contacts-v2__title--with-icon">
                                <span className="obd-section-head-icon obd-section-head-icon--sm" aria-hidden>
                                    <UsersThree size={16} weight="duotone" />
                                </span>
                                Contacts
                            </h3>
                            <div className="obd-contacts-modal-split__list-head-actions">
                                {!loading ? (
                                    <span className="obd-contacts-modal-split__count">
                                        {mergedCustomers.length}
                                    </span>
                                ) : null}
                                <button
                                    type="button"
                                    className="obd-contacts-modal-split__add-btn"
                                    onClick={() => setModalContactIndex(customers.length)}
                                    aria-label="Add contact"
                                >
                                    <UserPlus size={15} weight="bold" aria-hidden />
                                    <span>Add</span>
                                </button>
                            </div>
                        </div>
                        <div className="obd-contacts-modal-split__list-scroll obd-scroll-minimal">
                            {contactRows}
                        </div>
                    </div>
                </div>
                <div className="obd-contacts-modal-split__detail">
                    <div className="obd-contacts-modal-split__detail-panel">
                        {detailOpen ? (
                            <div className="obd-contacts-modal-split__detail-toolbar">
                                <button
                                    type="button"
                                    className="obd-contacts-modal-split__back"
                                    onClick={() => setModalContactIndex(null)}
                                >
                                    <CaretLeft size={14} weight="bold" aria-hidden />
                                    <span>All contacts</span>
                                </button>
                                {detailHeading ? (
                                    <span className="obd-contacts-modal-split__detail-heading">{detailHeading}</span>
                                ) : null}
                            </div>
                        ) : null}
                        {modalContactIndex !== null ? (
                            <div className="obd-contacts-modal-split__detail-editor">
                                <div className="obd-contacts-modal-split__detail-hero" aria-hidden={false}>
                                    <span className="obd-contacts-modal-split__detail-hero-avatar">{detailInitials}</span>
                                    <div className="obd-contacts-modal-split__detail-hero-copy">
                                        {(() => {
                                            const heroDisplayTags = activeDetailDisplay
                                                ? getHeroDisplayTags(activeDetailDisplay.tags)
                                                : [];
                                            const heroSubline =
                                                modalContactIndex !== null &&
                                                modalContactIndex >= mergedCustomers.length
                                                    ? 'New contact'
                                                    : activeDetailDisplay?.phone || null;
                                            return (
                                                <>
                                                    <div className="obd-contacts-modal-split__detail-hero-head">
                                                        <span className="obd-contacts-modal-split__detail-hero-name">
                                                            {detailHeading}
                                                        </span>
                                                    </div>
                                                    {heroDisplayTags.length > 0 ? (
                                                        <div
                                                            className="obd-contacts-modal-split__detail-hero-tags-row"
                                                            role="list"
                                                            aria-label="Contact roles"
                                                        >
                                                            {heroDisplayTags.map((t) => (
                                                                <span
                                                                    key={t.key}
                                                                    className={`obd-contacts-modal-split__detail-hero-tag obd-contacts-modal-split__detail-hero-tag--${t.tone}`}
                                                                    role="listitem"
                                                                >
                                                                    {formatModalRoleLabel(t)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                    {heroSubline ? (
                                                        <span className="obd-contacts-modal-split__detail-hero-tags obd-contacts-modal-split__detail-hero-tags--muted">
                                                            {heroSubline}
                                                        </span>
                                                    ) : null}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                {contactEditor}
                            </div>
                        ) : (
                            <div className="obd-contacts-modal-split__detail-empty">
                                <span className="obd-contacts-modal-split__detail-empty-icon" aria-hidden>
                                    <UserCircle size={32} weight="duotone" />
                                </span>
                                <p className="obd-contacts-modal-split__detail-empty-title">Select a contact</p>
                                <p className="obd-contacts-modal-split__detail-empty-hint">
                                    Pick someone from the list to view or edit their details, or add a new contact
                                    below.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`obd-card obd-card--contacts obd-card--contacts-v2 obd-dashboard-main-card ${isOpportunityInfoDisabled ? 'obd-disabled-section' : ''}`}>
            {contextHolder}
            <header className="obd-dashboard-main-card__head">
                <div className="obd-contacts-v2__header-row">
                    <h3 className="obd-contacts-v2__title obd-contacts-v2__title--with-icon">
                        <span className="obd-section-head-icon obd-section-head-icon--sm" aria-hidden>
                            <UsersThree size={18} weight="duotone" />
                        </span>
                        Contacts
                    </h3>
                    <div className="obd-contacts-v2__header-lead">
                        <span className="obd-section-head-icon obd-section-head-icon--sm" aria-hidden>
                            <IdentificationBadge size={18} weight="duotone" />
                        </span>
                        <div className="obd-contacts-v2__header-lead-text">
                            <span className="obd-contacts-v2__lead-label">Lead &amp; Title</span>
                            <span className="obd-contacts-v2__lead-primary">{leadSource || '—'}</span>
                        </div>
                    </div>
                </div>
            </header>

            <div className="obd-dashboard-main-card__body">{contactRows}</div>

            {contactsFooter}

            <TitleInfoModal
                open={titleInfoModalOpen}
                onClose={() => setTitleInfoModalOpen(false)}
                raw={titleInfo}
                theme={theme}
            />

            {contactEditor}
        </div>
    );
};

export default ContactsSnapshotCard;
